import React, { useRef, useState } from "react";
import styles from './index.module.css';
import { useDispatch, useSelector } from "react-redux";
import { showToast } from "../../../../components/ToastMsg";
import { useParams } from "react-router-dom";
import { addNewVideo } from "../../../../redux/features/courses/coursesService";
import BeatLoader from "react-spinners/BeatLoader";

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const Video = () => {

    const dispatch = useDispatch();
    const { id } = useParams();
    const fileInputRef = useRef(null);
    const documentInputRef = useRef(null);

    const { isVideoCreating } = useSelector((state) => state.course.newVideo);

    const [formData, setFormData] = useState({
        title: "",
    });

    const [errorMessages, setErrorMessages] = useState({});
    const [disabledBtn, setDisabledBtn] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrorMessages((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const validateDocument = (file) => {
        if (file) {
            if (!file.type.startsWith("application/pdf")) {
                setErrorMessages({ ...errorMessages, doc: "Only pdf files are allowed." });
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                setErrorMessages({ ...errorMessages, doc: "File size exceeds 2MB limit." });
                return;
            }

            setErrorMessages({ ...errorMessages, doc: "" });
            setFormData({ ...formData, doc: file });
        }
    };

    const handleDropDocument = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.dataTransfer.files[0];

        if (selectedFile) {
            validateDocument(selectedFile);
        }
    };

    const handleDocumentChange = (event) => {
        if (event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            validateDocument(selectedFile);
        }
    };

    const handleClickDocument = () => {
        documentInputRef.current.click();
    };

    const validateFile = (file) => {
        if (file) {
            if (!file.type.startsWith("image/")) {
                setErrorMessages({ ...errorMessages, thumbnail: "Only image files are allowed." });
                return;
            }

            if (file.size > MAX_FILE_SIZE) {
                setErrorMessages({ ...errorMessages, thumbnail: "File size exceeds 2MB limit." });
                return;
            }

            setErrorMessages({ ...errorMessages, thumbnail: "" });
            setFormData({ ...formData, thumbnail: file });
        }
    };

    const handleDrop = (event) => {
        event.preventDefault();
        event.stopPropagation();

        const selectedFile = event.dataTransfer.files[0];

        if (selectedFile) {
            validateFile(selectedFile);
        }
    };

    const handleFileChange = (event) => {
        if (event.target.files.length > 0) {
            const selectedFile = event.target.files[0];
            validateFile(selectedFile);
        }
    };

    const handleClick = () => {
        fileInputRef.current.click();
    };

    // Validate input
    const isValidVideo = () => {
        const errors = {};
        const trimmedName = formData.title.trim();
        if (!trimmedName) {
            errors.title = "Name is required.";
        }
        if (!formData.description) {
            errors.description = "Description is required.";
        }
        if (!formData.video_url) {
            errors.video_url = "Video Url is required.";
        }
        if (!formData.slug) {
            errors.slug = "Slug is required.";
        }
        if (!formData.order) {
            errors.order = "Order is required.";
        }
        if (Object.keys(errors).length > 0) {
            setErrorMessages(errors);
            return false;
        } else {
            return true;
        }
    };

    const insertNewVideo = async () => {
        if (isValidVideo()) {
            setDisabledBtn(true);
            try {
                const response = await dispatch(addNewVideo([formData, id])).unwrap();
                setDisabledBtn(false);
                setFormData({});
                showToast('success', response.message);
            }
            catch (error) {
                setDisabledBtn(false);
                showToast('error', error.message);
            };
        }
    };

    return (
        <div className={`card-section`}>
            <div className={`main-card`}>
                <h1 className={`form-title`}>Add New Video</h1>
                <div className={styles.formContainer}>
                    <form className={styles.coordinatorForm} noValidate>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">
                                        Title<span className="required-star">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="title"
                                        value={formData.title}
                                        onChange={handleInputChange}
                                        placeholder="Enter Title"
                                        className="form-input-default"
                                    />
                                    {errorMessages.title && (
                                        <p className="text-danger">{errorMessages.title}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">
                                        Description<span className="required-star">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleInputChange}
                                        placeholder="Enter Description"
                                        className="form-input-default"
                                    />
                                    {errorMessages.description && (
                                        <p className="text-danger">{errorMessages.description}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">Read More</label>
                                    <textarea
                                        value={formData.read_more}
                                        name="read_more"
                                        onChange={handleInputChange}
                                        className="form-input-default"
                                        rows={3}
                                        placeholder="Describe the video"
                                    ></textarea>
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Thumbnail<span className={`required-star`}>*</span>
                                    </label>
                                    <div
                                        onDrop={handleDrop}
                                        onDragOver={(event) => event.preventDefault()}
                                        onClick={handleClick}
                                        className={`drop-input`}
                                    >
                                        <input
                                            type="file"
                                            ref={fileInputRef}
                                            accept="image/*"
                                            onChange={handleFileChange}
                                            style={{ display: "none" }}
                                        />
                                        {(!formData.thumbnail) ? <p>Drag & Drop a file here or click to select</p>
                                            :
                                            <div className={`inserted-file`}>
                                                <div className={`file-card`}>
                                                    <img src={URL.createObjectURL(formData.thumbnail)} alt='File Type' className={`left-icon`} />
                                                </div>
                                            </div>
                                        }
                                    </div>
                                    {errorMessages.thumbnail && (
                                        <p className={`text-danger`}>{errorMessages.thumbnail}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">
                                        Document Title
                                    </label>
                                    <input
                                        type="text"
                                        name="doc_title"
                                        value={formData.doc_title}
                                        onChange={handleInputChange}
                                        placeholder="Enter Document Title"
                                        className="form-input-default"
                                    />
                                    {errorMessages.doc_title && (
                                        <p className="text-danger">{errorMessages.doc_title}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className={`form-input-row`}>
                            <div className={`form-input-col`}>
                                <div className={`form-input-group`}>
                                    <label htmlFor='full_name' className={`form-label`}>
                                        Document
                                    </label>
                                    <div
                                        onDrop={handleDropDocument}
                                        onDragOver={(event) => event.preventDefault()}
                                        onClick={handleClickDocument}
                                        className={`drop-input`}
                                    >
                                        <input
                                            type="file"
                                            ref={documentInputRef}
                                            accept="application/pdf"
                                            onChange={handleDocumentChange}
                                            style={{ display: "none" }}
                                        />
                                        {(!formData.doc) ? <p>Drag & Drop a file here or click to select</p>
                                            :
                                            <div className={`inserted-file`}>
                                                <div className={`file-card`}>
                                                    <embed
                                                        src={URL.createObjectURL(formData.doc)}
                                                        type="application/pdf"
                                                        width="100%"
                                                        height="200px"
                                                    />
                                                </div>
                                            </div>
                                        }
                                    </div>
                                    {errorMessages.doc && (
                                        <p className={`text-danger`}>{errorMessages.doc}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">
                                        Video URL<span className="required-star">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="video_url"
                                        value={formData.video_url}
                                        onChange={handleInputChange}
                                        placeholder="Enter Document Title"
                                        className="form-input-default"
                                    />
                                    {errorMessages.video_url && (
                                        <p className="text-danger">{errorMessages.video_url}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">
                                        Slug<span className="required-star">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        name="slug"
                                        value={formData.slug}
                                        onChange={handleInputChange}
                                        placeholder="Enter Document Title"
                                        className="form-input-default"
                                    />
                                    {errorMessages.slug && (
                                        <p className="text-danger">{errorMessages.slug}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="form-input-row">
                            <div className="form-input-col md-col">
                                <div className="form-input-group">
                                    <label className="form-label">
                                        Order<span className="required-star">*</span>
                                    </label>
                                    <input
                                        type="number"
                                        name="order"
                                        value={formData.order}
                                        onChange={handleInputChange}
                                        onKeyDown={(e) => {
                                            const allowedKeys = [
                                                "Backspace",
                                                "Delete",
                                                "ArrowLeft",
                                                "ArrowRight",
                                                "Tab",
                                            ];

                                            // If key is a number, allow it
                                            if (/^[0-9]$/.test(e.key)) return;

                                            // Allow control keys
                                            if (allowedKeys.includes(e.key)) return;

                                            // Block everything else (letters, punctuation, symbols)
                                            e.preventDefault();
                                        }}
                                        placeholder="Enter the order"
                                        className="form-input-default"
                                        min={1}
                                    />
                                    {errorMessages.order && (
                                        <p className="text-danger">{errorMessages.order}</p>
                                    )}
                                </div>
                            </div>
                        </div>
                        <button
                            className="btn-primary"
                            type="button"
                            disabled={disabledBtn}
                            onClick={insertNewVideo}
                        >
                            {isVideoCreating ? <BeatLoader color='#ffffff' /> : `Create`}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Video;